import { Chip, Typography } from '@/ui';

import type { AssistantAppointment } from './api';
import type { FC, ReactNode } from 'react';

const KIND_LABELS: Record<AssistantAppointment['kind'], string> = {
  chemistry: 'Chemistry call',
  coaching: 'Coaching session',
};

const PENDING_LABELS: Partial<
  Record<AssistantAppointment['lifecycleStatus'], string>
> = {
  cancellation_pending: 'Cancellation pending',
  reschedule_pending: 'Reschedule pending',
};

/** The viewer's own time zone: the assistant books against their calendar. */
export const formatAssistantAppointmentTime = (
  appointment: Pick<AssistantAppointment, 'endAt' | 'startAt'>,
  timeZone?: string,
): { date: string; time: string } => {
  const start = new Date(appointment.startAt);
  const end = new Date(appointment.endAt);
  const date = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone,
    weekday: 'long',
  }).format(start);
  const startTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(start);
  const endTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  }).format(end);
  return { date, time: `${startTime} – ${endTime}` };
};

interface AssistantAppointmentCardProps {
  /** Reschedule and cancel, one occurrence each. */
  actions?: ReactNode;
  appointment: AssistantAppointment;
}

export const AssistantAppointmentCard: FC<AssistantAppointmentCardProps> = ({
  actions,
  appointment,
}) => {
  const { date, time } = formatAssistantAppointmentTime(appointment);
  const pending = PENDING_LABELS[appointment.lifecycleStatus];

  return (
    <article
      aria-label={`${KIND_LABELS[appointment.kind]} on ${date}`}
      className="flex flex-col gap-2 rounded-xl border border-cement-300 p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="subtitle1">{date}</Typography>
        {pending ? <Chip label={pending} size="small" /> : null}
      </div>
      <Typography variant="body1">{time}</Typography>
      <Typography color="text.secondary" variant="body2">
        {KIND_LABELS[appointment.kind]}
        {appointment.coachDisplayName
          ? ` with ${appointment.coachDisplayName}`
          : ''}
        {appointment.seriesId ? ' · Recurring' : ''}
      </Typography>
      {actions ? <div className="flex gap-2 pt-2">{actions}</div> : null}
    </article>
  );
};
